import { expeditionLinks } from "./expeditionLinks";

export type Project = {
  slug: string; title: string; mark: string; type: string; stack: string[];
  card: string;
  // Home card artwork; pixel art keeps hard edges when scaled.
  cardImage?: { src: string; pixel?: boolean; position?: string };
  // Shown only by the shared Detail template; projects with their own detail page leave them out.
  summary?: string; overview?: string; role?: string[];
  builds?: { title: string; text: string }[];
  issue?: { title: string; problem: string; fix: string; result: string };
  links: { title: string; url: string }[]; recreation?: boolean; period?: string;
};

export const projects: Project[] = [
  {
    slug: "expedition-33", title: "Expedition 33", mark: "E33", type: "팀 프로젝트 · 모작",
    stack: ["C++20", "DirectX 11", "PhysX"],
    cardImage: { src: "/images/expedition-33/home-card.jpg", position: "center 35%" },
    card: "모델·애니메이션 파이프라인과 화면 효과를 자체 엔진의 게임플레이에 연결했습니다.",
    summary: "캐릭터 데이터가 변환기에서 런타임까지 이어지도록 만들고, 애니메이션과 화면 효과를 구현했습니다.",
    overview: "Clair Obscur: Expedition 33을 학습 목적으로 재현한 팀 프로젝트입니다. 모델·애니메이션 데이터 흐름과 캐릭터 동작, 화면 효과의 일부를 담당했습니다.",
    role: ["Assimp 기반 모델·애니메이션 변환기와 뷰어", "캐릭터별 애니메이션 상태 전환과 Root Motion", "Render Target/MRT 기반 화면 효과와 Client 연동"],
    builds: [
      { title: "모델에서 플레이까지", text: "Mesh, Material, Bone, Weight와 Animation 데이터를 자체 엔진 포맷으로 변환했습니다. 캐릭터별 경로와 상태를 매핑하고 Root Motion을 런타임 이동에 연결했습니다." },
      { title: "애니메이션 합성", text: "기본 애니메이션 포즈를 계산한 뒤 Modifier를 평가하고 본 결합 행렬을 다시 계산하도록 순서를 분리했습니다. ChainModifier의 관성·감쇠·제약과 PhysicsModifier의 결과를 본 포즈에 반영했습니다." },
      { title: "화면 효과", text: "두 Render Target을 교대로 쓰는 Ping-Pong 합성 경로를 만들고 전환 마스크, 블러, Glow와 RoseFlare 효과를 UI 렌더링 이전에 적용했습니다." }
    ],
    issue: { title: "캐릭터별 애니메이션 미스매치", problem: "스켈레톤 구조가 달라 원본 애니메이션을 바로 적용하면 포즈와 이동이 맞지 않았습니다.", fix: "UE5 리타겟 결과를 캐릭터별로 변환하고 AnimState 매핑과 Root Motion 보정을 연결했습니다.", result: "서로 다른 스켈레톤의 애니메이션을 같은 런타임 흐름에서 사용할 수 있게 했습니다." },
    links: [{ title: "YouTube 발표 영상", url: expeditionLinks.youtube }, { title: "담당 코드 GitHub", url: expeditionLinks.source }], recreation: true
  },
  {
    slug: "nier-automata", title: "NieR: Automata", mark: "N:A", type: "개인 프로젝트 · 모작",
    stack: ["C++20", "DirectX 11", "HLSL"],
    cardImage: { src: "/images/nier-automata/home-card.jpg", position: "center 40%" },
    card: "니어의 오픈월드 아포칼립스를 구현해 보고 싶었습니다. 넓은 폐허와 거대 보스를 60 FPS 이상으로 돌리도록 엔진을 리팩토링했습니다.",
    links: [{ title: "GitHub", url: "https://github.com/SoExDobin/NieRAutomata-Portfolio" }, { title: "YouTube 리팩토링 영상", url: "https://youtu.be/EFdMzHZHLIs" }], recreation: true
  },
  {
    slug: "pagomoa", title: "PagoMoa", mark: "PM", type: "팀 프로젝트",
    stack: ["Unity", "C#", "Ink"],
    card: "이벤트 기반 퀘스트와 인벤토리, 대화가 포함된 컷신의 상태 복원을 담당했습니다.",
    summary: "Unity 2D 팀 프로젝트에서 퀘스트, 인벤토리와 컷신·대화의 연결을 구현했습니다.",
    overview: "Unity 기반 2D 게임 공동 개발 프로젝트입니다. 팀 게임 중 퀘스트 조건과 보상, 인벤토리 조작, 컷신과 대화 및 UI 상태를 담당했습니다.",
    role: ["이벤트 기반 조건형 퀘스트와 ScriptableObject 제작 데이터", "인벤토리·퀵슬롯·장비·상점 흐름", "Timeline·Ink 대화와 컷신 상태 복원"],
    builds: [
      { title: "조건형 퀘스트", text: "필요한 게임플레이 이벤트만 구독하고 조건별 진행률과 완료 판정을 공통 흐름으로 묶었습니다. ScriptableObject와 Custom Inspector로 조건과 보상을 편집하게 했습니다." },
      { title: "인벤토리 상태", text: "아이템 추가, 분할, 합치기, 이동, 사용과 판매를 슬롯 데이터에서 처리하고 퀵슬롯과 장비를 실제 수량에 연결했습니다." },
      { title: "대화가 포함된 컷신", text: "Timeline 마커에서 재생을 멈추고 Ink 대화를 실행한 뒤 재개했습니다. 카메라, 플레이어 표시와 UI 상태를 종료 시 복원했습니다." }
    ],
    issue: { title: "컷신 종료 후 상태 복원", problem: "대화·Timeline·장면 전환의 종료 경로에 따라 카메라와 UI가 컷신 상태로 남을 수 있었습니다.", fix: "CutSceneController에 시작·대화·재개·종료 순서를 모으고 관련 상태를 함께 복원했습니다.", result: "대화가 포함된 컷신 이후에도 플레이 상태가 이어지도록 구성했습니다." },
    links: []
  },
  {
    slug: "moonlighter", title: "Moonlighter", mark: "ML", type: "팀 프로젝트 · 모작",
    stack: ["C++", "DirectX 9"],
    cardImage: { src: "/images/moonlighter/home-card.jpg", position: "center 40%" },
    card: "충돌 관계의 생명주기와 씬 전환, 사막 보스의 이동·공격 흐름을 구현했습니다.",
    links: [{ title: "GitHub", url: "https://github.com/SoExDobin/Moonlighter-Portfolio" }, { title: "YouTube 시연 영상", url: "https://www.youtube.com/watch?v=h-GAey4My9A&list=PLB_KDWv0W5Qo&index=2" }], recreation: true
  },
  {
    slug: "metal-slug-3", title: "Metal Slug 3", mark: "MS3", type: "개인 프로젝트 · 모작",
    period: "2025.09.15 ~ 2025.10.03 · 19일",
    stack: ["C++", "Win32 API", "FMOD"],
    cardImage: { src: "/images/metal-slug-3/home-card.png", pixel: true },
    card: "Win32 API로 미션 4의 플레이어 전투와 스테이지 진행을 구현했습니다.",
    summary: "《Metal Slug 3》 미션 4를 Win32 API로 재현한 개인 프로젝트입니다.",
    overview: "Win32 API와 GDI로 《Metal Slug 3》 미션 4를 재현했습니다. 플레이어 조작부터 충돌 판정, 화면 스크롤과 장면 전환까지 직접 구현했습니다.",
    role: ["플레이어 이동·점프·사격·근접 공격과 무기별 발사 동작", "충돌·플랫폼 판정과 구간별 화면 스크롤", "사막·피라미드·보스 장면의 적 배치와 전환 트리거"],
    builds: [
      { title: "플레이어 전투", text: "이동·점프·사격·근접 공격을 상태와 프레임 애니메이션에 연결했습니다. 무기에 따라 발사 동작과 투사체를 나눴습니다." },
      { title: "미션 4 진행", text: "구간별 스크롤 제한과 트리거를 두고, 적 배치와 사막·피라미드·보스 장면 전환을 구성했습니다." }
    ],
    issue: { title: "스테이지 구간과 화면 스크롤", problem: "미션 구간마다 이동 가능한 화면 범위와 등장할 적이 달랐습니다.", fix: "구간 트리거에서 스크롤 제한을 갱신하고 해당 구간의 적을 배치했습니다.", result: "플레이어가 진행한 구간에 맞춰 화면과 적 구성이 바뀌도록 연결했습니다." },
    links: [
      { title: "구현 브랜치", url: "https://github.com/SoExDobin/StudyMetalSlug3/tree/feat/day1-player-and-setup" },
      { title: "플레이어 코드", url: "https://github.com/SoExDobin/StudyMetalSlug3/blob/feat/day1-player-and-setup/MetalSlug3/CEri.h" },
      { title: "미션 4 코드", url: "https://github.com/SoExDobin/StudyMetalSlug3/blob/feat/day1-player-and-setup/MetalSlug3/CMission4.cpp" }
    ], recreation: true
  },
  {
    slug: "slimedicer", title: "SlimeDicer", mark: "SD", type: "개인 게임 프로젝트",
    period: "2022.11 ~ 2022.12 · 약 1개월",
    stack: ["Java 17", "Swing", "AWT"],
    cardImage: { src: "/images/slimedicer/Direme.png", pixel: true },
    card: "Java로 직접 만든 주사위 턴제 게임. 브라우저에서 바로 플레이할 수 있습니다.",
    summary: "Java Swing/AWT로 제작한 주사위 턴제 게임입니다. 웹 플레이 데모도 제공합니다.",
    overview: "Java 프로젝트에서 평소 만들어보고 싶던 게임을 택했습니다. Eclipse에서 Java 17과 Swing/AWT로 만든 1인용 주사위 턴제 게임입니다.",
    role: ["Swing/AWT 기반 게임 UI와 이벤트 처리", "주사위 점수의 공격·방어 배분과 턴제 전투", "시작·선택·결과 화면 흐름"],
    builds: [
      { title: "주사위 전투", text: "주사위 눈의 합 안에서 공격과 방어를 정하고, 적의 능력치와 비교해 피해와 승패를 계산했습니다." },
      { title: "게임 화면", text: "로비와 캐릭터 선택부터 전투, 결과 화면까지 구성하고 Swing 이벤트로 화면을 연결했습니다." }
    ],
    issue: { title: "전투 규칙과 화면 결과 연결", problem: "주사위 합계, 공격·방어 배분, 체력 변화와 승패를 한 턴에서 일관되게 처리해야 했습니다.", fix: "주사위 결과와 배분값으로 전투 수치를 계산하고 결과 평가를 화면 전환에 연결했습니다.", result: "입력부터 결과 화면까지 턴제 플레이 흐름을 구성했습니다." },
    links: [{ title: "공개 소스 저장소", url: "https://github.com/SoexDobin/SlimeDicer" }]
  }
];
